import {uniq} from 'ramda';

import type {
  ConfigurationSelection,
  ConfiguratorBundleResponseBody,
  TrimFeatureAssignment,
} from '@dms/api/new-vehicle';
import {FEATURE_CATEGORIES} from '@dms/features/new-vehicle';

import {isSelectionConditionMet} from '../../../../utils/isSelectionConditionMet';

/**
 * Standard equipment the selected configuration actually gets, grouped by category.
 * A STANDARD assignment counts only when its condition holds for the selected variant and the
 * build: the selected features plus the members of selected packages that apply to the trim, as
 * in the backend ConfigurationEvaluator. `requires` rules are not in the public bundle; saved
 * selections already carry the features they added.
 */
export const getStandardEquipment = (
  assignments: TrimFeatureAssignment[],
  bundle: Pick<ConfiguratorBundleResponseBody, 'features' | 'packages'>,
  selection: Pick<ConfigurationSelection, 'trimId' | 'variantId' | 'featureIds' | 'packageIds'>
) => {
  const build = [
    ...selection.featureIds,
    ...bundle.packages
      .filter(
        ({id, applicableTrimIds}) =>
          selection.packageIds.includes(id) &&
          (applicableTrimIds.length === 0 || applicableTrimIds.includes(selection.trimId))
      )
      .flatMap(({memberFeatureIds}) => memberFeatureIds),
  ];
  const standardIds = uniq(
    assignments
      .filter(
        ({inclusion, appliesIf}) =>
          inclusion === 'STANDARD' && isSelectionConditionMet(appliesIf, selection.variantId, build)
      )
      .toSorted((first, second) => first.sortOrder - second.sortOrder)
      .map(({featureId}) => featureId)
  );
  const standard = standardIds.flatMap((id) =>
    bundle.features.filter((feature) => feature.id === id)
  );

  return FEATURE_CATEGORIES.map((category) => ({
    category,
    features: standard
      .filter((feature) => feature.category === category)
      .map(({id, name}) => ({id, name})),
  })).filter(({features}) => features.length > 0);
};
